import {
  Button,
  ButtonColorEnum,
  ClockIcon,
  IconButton,
  IconButtonColorEnum,
  IconSizeEnum,
  Tooltip,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { TableRow } from '@/components/TableRow';
import { buildNewPresenceDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  FrameAction,
  FrameActionType,
  PostPresenceFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { formatDateCustom } from '@/helpers/format-date';
import { useNotificationStore } from '@/stores/notifications';
import { TPresenceItem } from '@/types/presences';

type TimelineEndInlineControlProps<T extends TPresenceItem> = {
  item: T;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostPresenceFor<T>>;
  onPreview?: (item: T, frame?: FrameActionType) => void;
  buildDataToSave?: (item: T) => PostPresenceFor<T>;
  presenceData?: { id?: string };
};
export function TimelineEndInlineControl<T extends TPresenceItem>({
  item,
  onUpdateInline,
  onPreview,
  buildDataToSave,
  presenceData,
}: TimelineEndInlineControlProps<T>) {
  const { logoData, playerVideoCurrentTime, getDateTimeCurrentPresence } =
    useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { addErrorNotification } = useNotificationStore();

  const activeData = presenceData ?? logoData;
  const dataToSave = (buildDataToSave ?? buildNewPresenceDataToSave) as (
    item: T
  ) => PostPresenceFor<T>;

  const setNewEndDateTime = () => {
    if (activeData?.id !== item.id) {
      onPreview?.(item, FrameAction.END);
    }

    const newData = dataToSave(item);
    const newDateTime = getDateTimeCurrentPresence(playerVideoCurrentTime);
    const applySilently = false;
    const { start_datetime: startDatetime } = newData;

    if (newDateTime && startDatetime && startDatetime > newDateTime) {
      addErrorNotification(
        t(
          'El tiempo para marcar la finalización de una presencia no puede ser anterior a su inicio'
        )
      );
      return;
    }

    newDateTime && (newData.end_datetime = newDateTime);
    onUpdateInline?.(newData, applySilently, playerVideoCurrentTime);
  };

  return (
    <div className="flex items-center gap-0.5">
      <Button
        className="min-h-13 bg-transparent p-2"
        color={ButtonColorEnum.SECONDARY_OLD}
        onClick={() => onPreview?.(item, FrameAction.END)}
      >
        <TableRow
          content={formatDateCustom({
            input: item.end_datetime,
            format: 'dd/MM/yyyy HH:mm:ss',
          })}
        />
      </Button>
      <Tooltip help={t('Marcar Fin en el frame de vídeo actual')} grow>
        <IconButton color={IconButtonColorEnum.NONE_DEFAULT} onClick={setNewEndDateTime}>
          <ClockIcon size={IconSizeEnum.SM} />
        </IconButton>
      </Tooltip>
    </div>
  );
}
